import { useEffect, useRef, useState } from "react";

// CSS media-query hook. Returns whether the query currently matches and
// re-renders on changes. Used to switch the request-builder split layout
// between horizontal and vertical at md+.
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const m = window.matchMedia(query);
    const cb = (e: MediaQueryListEvent) => setMatches(e.matches);
    m.addEventListener("change", cb);
    return () => m.removeEventListener("change", cb);
  }, [query]);
  return matches;
}


interface Opts {
  axis: "x" | "y";
  initial: number;
  min: number;
  max: number;
  storageKey: string;
  /** If set, drag deltas are inverted (e.g. dragging a handle on the LEFT
   *  edge of a right-side panel should *shrink* it when the pointer moves
   *  right). */
  invert?: boolean;
}

// Drag-to-resize. Returns the current size (in pixels), a setter, and the
// props to spread onto the visual drag handle. The size persists to
// localStorage so the user's preference survives reloads.
export function useResizable({ axis, initial, min, max, storageKey, invert = false }: Opts) {
  const [size, setSize] = useState<number>(() => {
    try {
      const v = localStorage.getItem(storageKey);
      const n = v != null ? parseFloat(v) : NaN;
      if (Number.isFinite(n) && n >= min && n <= max) return n;
    } catch { /* ignore */ }
    return initial;
  });
  useEffect(() => {
    try { localStorage.setItem(storageKey, String(size)); } catch { /* ignore */ }
  }, [size, storageKey]);

  // Active drag — captured at pointerdown so subsequent moves compute delta
  // from a stable starting point.
  const dragRef = useRef<{ startPos: number; startSize: number } | null>(null);

  const onPointerDown = (e: React.PointerEvent) => {
    // Only respond to primary button (mouse left / touch / pen).
    if (e.button !== 0) return;
    e.preventDefault();
    dragRef.current = {
      startPos: axis === "x" ? e.clientX : e.clientY,
      startSize: size,
    };
    document.body.style.cursor = axis === "x" ? "col-resize" : "row-resize";
    document.body.style.userSelect = "none";
    const onMove = (ev: PointerEvent) => {
      const start = dragRef.current;
      if (!start) return;
      const pos = axis === "x" ? ev.clientX : ev.clientY;
      const delta = (pos - start.startPos) * (invert ? -1 : 1);
      const next = Math.max(min, Math.min(max, start.startSize + delta));
      setSize(next);
    };
    const onUp = () => {
      dragRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  };

  return { size, setSize, dragProps: { onPointerDown } };
}
